import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import {
  CrmObjectInterface,
  FieldSchemaInterface,
  FieldType,
  FieldValueInterface,
  FilterOperator,
  ListCrmObjectsResponseInterface,
} from '@vendasta/crm';
import { GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  CellData,
  DateData,
  GalaxyDataSource,
  PagedListRequestInterface,
  PagedResponseInterface,
  PhoneData,
  Row,
  TextData,
} from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { BehaviorSubject, Observable, asyncScheduler, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, observeOn, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { TaskTableCustomizationService } from '../../shared-services/table-customization/task-table-customization.service';
import { CrmFormService } from '../../shared-services/crm-services/crm-form.service';
import { CrmObjectService } from '../../shared-services/crm-services/crm-object-service/crm-object.service';
import { CrmTableStateService } from '../../shared-services/crm-services/crm-object-service/crm-table-state.service';
import { ActivityType, ObjectType } from '../../tokens-and-interfaces';
import { LIST_OBJECTS_DEFAULT_PAGE_SIZE } from '../../constants';
import { CRMTrackingService } from '../../shared-services/page-analytics.service';
import { PreviewObjectService } from '../../shared-services/preview-object.service';
import { CrmInjectionToken, CrmDependencies } from '../../tokens-and-interfaces';
import {
  ColumnDataInterface,
  TableCustomizationServiceInterface,
  ContactTableCustomizationService,
  CompanyTableCustomizationService,
  SystemFieldIds,
} from '../../shared-services';

interface ColumnDef extends GalaxyColumnDef {
  external_id?: string;
}

@Injectable()
export class ListObjectsTableService implements OnDestroy {
  dataSource: GalaxyDataSource<Row, GalaxyFilterInterface, MatSort>;
  columns$: Observable<GalaxyColumnDef[]> = this.state.select('columns');
  groupConfigs$: Observable<GalaxyColumnDef[]> = this.state.select('groupConfigs');
  initialLoading$: Observable<boolean> = this.state.select('initialLoading');
  deletedObjectIds$: Observable<string[]> = this.state.select('deletedObjectIds');
  showEmptyState$: Observable<boolean> = combineLatest([
    this.initialLoading$,
    this.state.select('receivedObject'),
  ]).pipe(map(([loading, hasObjects]) => !loading && !hasObjects));
  showObjects$: Observable<boolean> = combineLatest([this.initialLoading$, this.showEmptyState$]).pipe(
    map(([loading, showEmptyState]) => !loading && !showEmptyState),
  );
  private data$$ = new BehaviorSubject<CrmObjectInterface[]>([]);
  readonly data$ = combineLatest([this.data$$, this.deletedObjectIds$]).pipe(
    map(([rows, deleted]) => rows.filter((r) => !deleted.includes(r.crmObjectId || ''))),
  );
  filters: GalaxyFilterInterface[] = [];
  sorting: MatSort[] = [];
  private baseColumnIds: string[] = [];
  private showActions = true;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly objectsService: CrmObjectService,
    private readonly fieldsService: CrmFormService,
    private readonly state: RxState<ComponentState>,
    private readonly translate: TranslateService,
    private readonly previewObjectService: PreviewObjectService,
    private readonly trackingService: CRMTrackingService,
    private readonly contactCustomization: ContactTableCustomizationService,
    private readonly companyCustomization: CompanyTableCustomizationService,
    private readonly taskCustomization: TaskTableCustomizationService,
    private readonly tableStateService: CrmTableStateService,
    private readonly snackService: SnackbarService,
    private readonly crmFormService: CrmFormService,
  ) {
    const initial: ComponentState = {
      objectType: 'Contact', // this must have a default value, but the service should be initialized with setObjectType
      fieldSchemas: [],
      columns: [],
      groupConfigs: [],
      initialLoading: true,
      receivedObject: false,
      deletedObjectIds: [],
    };
    this.state.set(initial);

    const schemas$: Observable<FieldSchemaInterface[]> = combineLatest([
      this.config.namespace$,
      this.state.select('objectType'),
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      switchMap(([, objectType]) => this.fieldsService.listAllObjectFields$(objectType)),
    );
    this.state.connect(
      schemas$.pipe(
        map((schemas) => {
          const newState: Partial<ComponentState> = {
            fieldSchemas: schemas,
          };
          return newState;
        }),
      ),
    );

    this.state.connect(
      schemas$.pipe(
        observeOn(asyncScheduler),
        map((schemas: FieldSchemaInterface[]) => schemas.map((schema) => this.fieldSchemaToTableColumn(schema))),
        switchMap((columns: GalaxyColumnDef[]) => combineLatest([this.applyColumnLayout$(columns)])),
        map(([columns]) =>
          this.customizedColumns(this.state.get('objectType'), columns, false, this.state.get('activityType')),
        ),
        map((columns) => this.addStaticColumns(columns)),
        switchMap((columns) => {
          return this.showBaseColumns(columns);
        }),
        switchMap((columns) => {
          return this.tableStateService.initializeLocalStorageColumns(columns);
        }),
        switchMap((columns) => this.addColumnGroups(columns)),
      ),
    );

    this.state.connect(
      this.config.namespace$.pipe(
        distinctUntilChanged(),
        map(() => ({ initialLoading: true })),
      ),
    );

    const paginatedAPI = {
      get: (
        req: PagedListRequestInterface<GalaxyFilterInterface, MatSort>,
      ): Observable<PagedResponseInterface<Row>> => {
        req.filters = this.filters;
        const results = this.listObjects(req).pipe(
          catchError(() => {
            this.snackService.openErrorSnack('ERRORS.GENERIC_MESSAGE');
            return of({
              crmObjects: [],
              pagingMetadata: {
                nextCursor: '',
                hasMore: false,
              },
            }) as Observable<ListCrmObjectsResponseInterface>;
          }),
          tap((resp) => {
            const receivedObject = this.state.get('receivedObject');
            if (!receivedObject && (resp?.crmObjects || []).length > 0) {
              this.state.set({ receivedObject: true });
            }
          }),
          tap(() => {
            if (req.searchOptions?.text) {
              this.trackingService.trackEvent(this.state.get('objectType'), 'searched-crm-object', {
                searchFields: req.searchOptions.fields,
              });
            }
          }),
          tap((resp) => this.data$$.next(resp.crmObjects || [])),
          withLatestFrom(this.state.select('fieldSchemas')),
          map(([objectListResp, schemas]) => {
            const rows = this.crmObjectsToTableRows(objectListResp?.crmObjects || [], schemas);
            return {
              data: rows,
              pagingMetadata: {
                nextCursor: objectListResp?.pagingMetadata?.nextCursor || '',
                hasMore: objectListResp?.pagingMetadata?.hasMore || false,
                totalResults: objectListResp?.pagingMetadata?.totalResults || Infinity,
              },
            } as PagedResponseInterface<Row>;
          }),
        );

        return combineLatest([results, this.state.select('deletedObjectIds')]).pipe(
          map(([rows, deleted]) => {
            rows.data = rows.data.filter((r) => !deleted.includes(r.id));
            return rows;
          }),
        );
      },
    };
    this.dataSource = new GalaxyDataSource<Row, GalaxyFilterInterface, MatSort>(paginatedAPI);
  }

  applyColumnLayout$(columns: ColumnDef[]): Observable<ColumnDef[]> {
    return this.fieldsService.listAllObjectFieldGroups$(this.state.get('objectType')).pipe(
      map((fieldGroups) => {
        const fieldIds: string[] = fieldGroups.reduce(
          (r: string[], fieldGroup) => r.concat(fieldGroup.fieldIds ?? []),
          [],
        );

        const sortedColumns: ColumnDef[] = JSON.parse(JSON.stringify(columns));
        sortedColumns.sort((a, b) => {
          let indexA = fieldIds.indexOf(a.id);
          if (indexA === -1 && a.external_id) {
            indexA = fieldIds.indexOf(a.external_id);
          }
          let indexB = fieldIds.indexOf(b.id);
          if (indexB === -1 && b.external_id) {
            indexB = fieldIds.indexOf(b.external_id);
          }
          if (indexA === -1) {
            return 1;
          }
          if (indexB === -1) {
            return -1;
          }
          return indexA - indexB;
        });

        const firstFieldGroupFieldIds = fieldGroups[0]?.fieldIds ?? [];
        for (const column of sortedColumns) {
          if (firstFieldGroupFieldIds.includes(column.id)) {
            continue;
          }
          column.hidden = true;
        }

        return sortedColumns;
      }),
    );
  }

  addStaticColumns(columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    if (!this.showActions) {
      return columns;
    }
    const actionsColumn: GalaxyColumnDef = {
      id: 'actions',
      title: this.translate.instant('LIST_OBJECTS_TABLE.ACTIONS_COLUMN.TITLE'),
      pinned: true,
      stickyEnd: true,
      sortable: false,
    };
    return [...columns, actionsColumn];
  }

  addColumnGroups(columns: GalaxyColumnDef[]): Observable<Partial<ComponentState>> {
    return this.crmFormService.listAllObjectFieldGroups$(this.state.get('objectType')).pipe(
      map((groups) => {
        const groupConfigs: GalaxyColumnDef[] = groups.map((group) => {
          return {
            id: this.translate.instant(group.fieldGroupId ?? 'Custom'),
            title: this.translate.instant(group.description ?? 'Custom fields'),
            columns: (group.fieldIds || []).map((fieldId) => {
              return columns.find((column) => {
                return column.id === fieldId;
              });
            }),
          };
        });
        return groupConfigs;
      }),
      map((groups) => {
        const newState: Partial<ComponentState> = {
          columns: columns,
          groupConfigs: groups,
        };
        return newState;
      }),
    );
  }

  private moveItemsToBeginning(columns: GalaxyColumnDef[], identifiersToMove: string[]): GalaxyColumnDef[] {
    const movedToFront: GalaxyColumnDef[] = [];

    for (const id of identifiersToMove) {
      const col = columns.find((c) => c.id === id);
      if (col) {
        movedToFront.push(col);
      }
    }
    const movedToBack = columns.filter((c) => !movedToFront.find((m) => m.id === c.id));
    return [...movedToFront, ...movedToBack];
  }

  showBaseColumns(columns: GalaxyColumnDef[]): Observable<GalaxyColumnDef[]> {
    return of(false).pipe(
      map((isMobile) => {
        if (isMobile) {
          return columns;
        }
        for (const col of this.baseColumnIds) {
          const index = columns.findIndex((c) => c.id === col);
          if (index > -1) {
            columns[index].hidden = false;
          }
        }
        return this.moveItemsToBeginning(columns, this.baseColumnIds);
      }),
    );
  }

  deleteObject(objectId: string): Observable<unknown> {
    return this.state.select('objectType').pipe(
      switchMap((objectType) => this.objectsService.deleteCrmObject(objectType, objectId)),
      withLatestFrom(this.state.select('deletedObjectIds')),
      tap(([, deleted]) => {
        this.state.set({ deletedObjectIds: [...deleted, objectId] });
        return this.state;
      }),
      take(1),
    );
  }

  setObjectType(objectType: ObjectType): void {
    this.state.set({ objectType: objectType });
  }

  setActivityType(activityType: ActivityType): void {
    this.state.set({ activityType: activityType });
  }

  setBaseColumns(baseColumnIds: string[]): void {
    this.baseColumnIds = baseColumnIds;
  }

  setShowActions(showActions: boolean): void {
    this.showActions = showActions;
  }

  private listObjects(
    req: PagedListRequestInterface<GalaxyFilterInterface, MatSort>,
  ): Observable<ListCrmObjectsResponseInterface> {
    return this.state.select('objectType').pipe(
      observeOn(asyncScheduler),
      tap(() =>
        this.state.set({
          deletedObjectIds: [],
        }),
      ),
      switchMap((objectType) => {
        const filterGroup = this.objectsService.convertFilters([...(req.filters || [])]);
        if (filterGroup && objectType === 'Activity') {
          const activityType = this.state.get('activityType');
          if (activityType) {
            filterGroup.filters?.push(this.getActivityTypeFilter(activityType));
          }
        }

        let sortBy = this.objectsService.convertSortBy(req.sorting);
        if (!sortBy?.length) {
          const objType = this.state.get('activityType') || this.state.get('objectType');
          sortBy = this.getTableCustomizationService(objType)?.defaultSortingOptions;
        }

        return this.objectsService.listObjects(objectType, {
          search: {
            searchTerm: req?.searchOptions?.text || '',
          },
          filtersV2: filterGroup,
          pagingOptions: {
            cursor: req?.pagingOptions?.cursor,
            pageSize: req?.pagingOptions?.pageSize,
          },
          sortBy: sortBy ?? [],
        });
      }),
      map((resp) => {
        // clear preview on user interaction
        if (!this.listRequestIsPristine(req)) {
          this.previewObjectService.clearPreview();
        } else {
          resp.crmObjects = this.previewObjectService.withPreview(resp.crmObjects || []);
        }
        return resp;
      }),
      tap(() => {
        this.state.set({ initialLoading: false });
      }),
    );
  }

  private getTableCustomizationService(
    objectType: ObjectType | ActivityType,
  ): TableCustomizationServiceInterface | undefined {
    switch (objectType) {
      case 'Contact':
        return this.contactCustomization;
      case 'Company':
        return this.companyCustomization;
      case 'Task':
        return this.taskCustomization;
    }
    return undefined;
  }

  private getActivityTypeFilter(activityType: ActivityType) {
    return {
      fieldId: SystemFieldIds.ActivityType,
      operator: FilterOperator.FILTER_OPERATOR_IS,
      values: [
        {
          string: activityType,
        },
      ],
    };
  }

  private fieldSchemaToTableColumn(schema: FieldSchemaInterface): ColumnDef {
    return {
      id: schema.fieldId || '',
      external_id: schema.externalId || '',
      title: schema.fieldName,
      sortable: true,
    };
  }

  private crmObjectsToTableRows(objects: CrmObjectInterface[], schemas: FieldSchemaInterface[]): Row[] {
    const schemaMap: { [schemaId: string]: FieldSchemaInterface } = {};
    schemas.forEach((schema) => {
      if (!schema?.fieldId) {
        return;
      }
      schemaMap[schema.fieldId] = schema;
    });

    const objType = this.state.get('activityType') || this.state.get('objectType');
    return (
      objects?.map((obj) => {
        const columnData: ColumnDataInterface = {};
        obj?.fields?.forEach((field) => {
          if (!field?.fieldId) {
            return;
          }
          columnData[field.fieldId] = this.getValueByType(field, schemaMap[field.fieldId]);
        });
        this.customizeRowData(objType, obj, schemaMap, columnData);

        return {
          id: obj?.crmObjectId || '',
          data: columnData,
        };
      }) || []
    );
  }

  private getValueByType(field: FieldValueInterface, schema: FieldSchemaInterface): CellData {
    switch (schema?.fieldType) {
      case FieldType.FIELD_TYPE_CURRENCY:
      case FieldType.FIELD_TYPE_INTEGER:
        return {
          cellType: 'integer',
          value: field.integerValue,
        };
      case FieldType.FIELD_TYPE_FLOAT:
        return {
          cellType: 'float',
          value: field.floatValue,
        };
      case FieldType.FIELD_TYPE_DATETIME:
      case FieldType.FIELD_TYPE_DATE:
        return this.dateData(field.dateValue);
      case FieldType.FIELD_TYPE_PHONE:
        return this.phoneData(field.stringValue);
      case FieldType.FIELD_TYPE_STRING:
      case FieldType.FIELD_TYPE_EMAIL:
        return this.textData(field.stringValue);
      case FieldType.FIELD_TYPE_DROPDOWN:
        return this.dropDownOptionData(schema, field.stringValue);
      case FieldType.FIELD_TYPE_BOOLEAN:
        return {
          cellType: 'boolean',
          value: field.booleanValue,
        };
      case FieldType.FIELD_TYPE_TAG:
      case FieldType.FIELD_TYPE_STRING_LIST:
        return {
          cellType: 'tags',
          value: field?.stringValues?.values || [],
        };
      default:
        return undefined;
    }
  }

  private dropDownOptionData(schema: FieldSchemaInterface, value?: string): TextData {
    const label = schema?.dropdownOptions?.find((o) => o?.value === value)?.label;
    return this.textData(label || value);
  }

  private textData(value?: string, link?: string): TextData {
    return {
      cellType: 'text',
      value: value || '',
      link: link || '',
    };
  }

  private phoneData(value?: string): PhoneData {
    return {
      cellType: 'phone',
      value: value,
    };
  }

  private dateData(value?: Date): DateData {
    return {
      cellType: 'date',
      value: value,
    };
  }

  private listRequestIsPristine(req: PagedListRequestInterface<GalaxyFilterInterface, MatSort>): boolean {
    return (
      !req?.pagingOptions?.cursor &&
      req?.pagingOptions?.pageSize === LIST_OBJECTS_DEFAULT_PAGE_SIZE &&
      !req?.searchOptions?.text &&
      !req?.searchOptions?.fields?.length &&
      !req?.filters?.length &&
      !req.sorting?.length
    );
  }

  private customizedColumns(
    objectType: ObjectType,
    columns: ColumnDef[],
    isMobile: boolean,
    activityType?: ActivityType,
  ): GalaxyColumnDef[] {
    return this.getTableCustomizationService(activityType || objectType)?.customizeColumns(columns, isMobile) || [];
  }

  private customizeRowData(
    objectType: ObjectType | ActivityType,
    obj: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ) {
    return this.getTableCustomizationService(objectType)?.customizeRowData(obj, schemaMap, columnData);
  }

  lookupRowDisplayName(id: string): Observable<string> {
    return this.data$$.pipe(
      map((rows) => {
        const row = rows.find((r) => r.crmObjectId == id);
        if (!row) {
          return id;
        }
        const firstName = row?.fields?.find((f) => f.externalId == 'standard__first_name')?.stringValue;
        const lastName = row?.fields?.find((f) => f.externalId == 'standard__last_name')?.stringValue;
        if (firstName || lastName) {
          return `${firstName} ${lastName}`.trim();
        }
        const email = row?.fields?.find((f) => f.externalId == 'standard__email')?.stringValue;
        if (email) {
          return email;
        }
        const phone = row?.fields?.find((f) => f.externalId == 'standard__phone_number')?.stringValue;
        if (phone) {
          return phone;
        }
        return id;
      }),
    );
  }

  ngOnDestroy(): void {
    this.previewObjectService.clearPreview();
  }
}

interface ComponentState {
  objectType: ObjectType;
  activityType?: ActivityType;
  fieldSchemas: FieldSchemaInterface[];
  columns: GalaxyColumnDef[];
  groupConfigs: GalaxyColumnDef[];
  initialLoading: boolean;
  receivedObject: boolean;
  deletedObjectIds: string[];
}
